/**
 * 重连请求与摘要。见 docs/ARCHITECTURE.md §5.5（hello.resume）与 §7.11。
 *
 * 这里只有类型。`buildResumeSummary` 的实现在 packages/server/src/room/resume.ts。
 */
import type { Card, RoomId, SessionId } from '../ids.js'

/**
 * `hello.resume` 的载荷。
 *
 * ★ 三个「上次进度」字段各管各的（与 §5.2 的 seq/sv/epoch 三元组同源）：
 *   - `lastServerSeq` 是**传输**进度，服务端据此决定重发缓冲还是直接推快照
 *     （缺口 > WS.RESUME_GAP_SNAPSHOT_THRESHOLD 就不补帧，直接快照）；
 *   - `lastSv` 是**房间状态**进度，只影响快照择新；
 *   - `lastHandNo` 是**牌局**进度，只用于算 `ResumeSummary.missedHands`。
 *   把它们合成一个数会让三种判断互相污染。
 *
 * `resumeToken` 是明文 bearer：服务端存 SHA-256，日志必须 redact（红线 #13）。
 */
export interface ResumeRequest {
  readonly sessionId: SessionId
  readonly resumeToken: string
  readonly lastServerSeq: number
  readonly roomId: RoomId
  readonly lastSv: number
  readonly lastHandNo: number
  /**
   * `autoReconnect` = 断线自动重连（静默恢复，`awayMs < RESUME.SILENT_RESUME_MS`
   * 且无错过手牌时 UI 不提示）；`userOpen` = 用户主动重新打开（总是展示摘要）。
   */
  readonly intent: 'autoReconnect' | 'userOpen'
}

/**
 * 「重连没能回到房间」的信号：hello 带着 `resume.roomId` 来，握手做完这条会话却没绑上任何房间
 * （旧会话已被回收、房间已经没了……）。网关回一条**不带 refCid** 的 `E_NOT_IN_ROOM`，
 * `details[RESUME_LOST_ROOM_KEY]` 是客户端想回的那个房间。
 *
 * ★ 客户端只认这一条去自动重进房，不能见到 E_NOT_IN_ROOM 就重进：房间里也会回这个码
 *   （要踢的人已经走了、转让房主的目标不在），那些跟「我这条连接在不在房里」无关。
 */
export const RESUME_LOST_ROOM_KEY = 'resumeRoomId'

export function resumeLostDetails(roomId: RoomId): Readonly<Record<string, unknown>> {
  return { [RESUME_LOST_ROOM_KEY]: roomId }
}

/** 是「重连没能回到房间」就返回那个房间 id，否则 null */
export function resumeLostRoomId(err: {
  readonly code: string
  readonly details?: Readonly<Record<string, unknown>>
}): RoomId | null {
  if (err.code !== 'E_NOT_IN_ROOM') return null
  const roomId = err.details?.[RESUME_LOST_ROOM_KEY]
  return typeof roomId === 'string' && roomId !== '' ? roomId : null
}

export type ResumeOutcome =
  /** 座位还在，直接坐回去 */
  | 'resumedSeat'
  /** 座位已被回收，筹码在 room_ledger.parked_stack 里等着 */
  | 'seatReleased'
  | 'roomClosed'
  | 'roomNotFound'
  /** 之前只是观众 / 从未入座 */
  | 'neverSeated'

export type ResumeHandOutcome = 'won' | 'lost' | 'folded' | 'notDealt' | 'voided'

export interface ResumeHandEntry {
  readonly handNo: number
  readonly yourOutcome: ResumeHandOutcome
  readonly delta: number
  /** voided 或未发牌时为 null */
  readonly board: readonly Card[] | null
}

/**
 * 断线期间发生了什么。UI 呈现见 §11.6。
 *
 * ★ 可计算定义（§7.11，原规格的字段语义无法实现，这里是裁决后的口径）：
 *   - `awayMs` = `now - max(该账号所有连接的 lastDisconnectAt)`。
 *     若期间一直有 shadow 连接则为 0，此时 `missedHands` 也应为 0，UI 不提示。
 *     **不要**用 `seat.status_since`——有 shadow 连接时它根本不变。
 *   - `netChipDelta` = `seat.stack(now) - seat.stackAtDisconnect`（座位进入
 *     `disconnected` 时记录），含断线时正在进行的那一手。
 *   - `missedHands` 只数 `hand_no > lastHandNo AND status='complete'` 的手牌，
 *     **排除 voided**；voided 手在 `hands[]` 里以 `yourOutcome:'voided', delta:0`
 *     出现但不计入 `missedHands`（红线 #25：voided 手不推进任何指针）。
 */
export interface ResumeSummary {
  readonly outcome: ResumeOutcome
  readonly awayMs: number
  readonly missedHands: number
  readonly netChipDelta: number
  readonly blindsPosted: number
  readonly autoFolds: number
  readonly missedYourTurn: boolean
  /** 服务端生成，保证中英文案一致（客户端不自行拼接） */
  readonly headline: string
  /** 最多 RESUME.SUMMARY_MAX_HANDS 条，按 handNo 倒序 */
  readonly hands: readonly ResumeHandEntry[]
  /** 仅 outcome === 'seatReleased' 时存在 */
  readonly parkedStack?: number
}
